import { c as _c } from "react/compiler-runtime";
import { Select, Tooltip } from '@onefootprint/ui';
import omit from 'lodash/omit';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EnvironmentIndicator from '@/components/environment-indicator';
import useOrgSession from '@/hooks/use-org-session';
type SandboxToggleProps = {
  onSandboxToggle?: (isSandbox: boolean) => void;
  /** When provided, the component is controlled and does not read from / write to org session. */
  value?: 'sandbox' | 'production';
  /** Called when the user picks an option in controlled mode. */
  onChange?: (value: 'sandbox' | 'production') => void;
  /** Override the disabled state. Defaults to !sandbox.canToggle from org session. */
  disabled?: boolean;
  /** Override the tooltip shown when disabled. Defaults to the org-level "no permission" copy. */
  disabledTooltip?: string;
  className?: string;
  labelClassName?: string;
  onOpenChange?: (open: boolean) => void;
};
export const SandboxToggle = t0 => {
  const $ = _c(51);
  const {
    onSandboxToggle,
    value,
    onChange,
    disabled,
    disabledTooltip,
    className,
    labelClassName,
    onOpenChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "components.privateLayout.nav.navHeader.sandboxToggle"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("common", t1);
  const router = useRouter();
  const {
    sandbox
  } = useOrgSession();
  const isControlled = value !== undefined;
  const [uncontrolledValue, setUncontrolledValue] = useState(sandbox.isSandbox ? "sandbox" : "production");
  const selectedValue = isControlled ? value : uncontrolledValue;
  const isDisabled = disabled ?? !sandbox.canToggle;
  let t2;
  let t3;
  if ($[1] !== isControlled || $[2] !== sandbox.isSandbox) {
    t2 = () => {
      if (!isControlled) {
        setUncontrolledValue(sandbox.isSandbox ? "sandbox" : "production");
      }
    };
    t3 = [sandbox.isSandbox, isControlled];
    $[1] = isControlled;
    $[2] = sandbox.isSandbox;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== isControlled || $[6] !== onChange || $[7] !== onSandboxToggle || $[8] !== router || $[9] !== sandbox) {
    t4 = async next => {
      const nextValue = next as 'sandbox' | 'production';
      if (isControlled) {
        onChange?.(nextValue);
        return;
      }
      setUncontrolledValue(nextValue);
      if (onSandboxToggle) {
        onSandboxToggle(nextValue === "sandbox");
      }
      if (router.pathname.startsWith("/users")) {
        router.push({
          pathname: "/users",
          query: omit(router.query, ["id", "cursor"])
        });
      }
      if (router.pathname.startsWith("/businesses")) {
        router.push({
          pathname: "/businesses",
          query: omit(router.query, ["id", "cursor"])
        });
      }
      await sandbox.toggle();
    };
    $[5] = isControlled;
    $[6] = onChange;
    $[7] = onSandboxToggle;
    $[8] = router;
    $[9] = sandbox;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleValueChange = t4;
  let t5;
  if ($[11] !== disabledTooltip || $[12] !== t) {
    t5 = disabledTooltip ?? t(_temp);
    $[11] = disabledTooltip;
    $[12] = t;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const t6 = !isDisabled;
  let t7;
  if ($[14] !== t) {
    t7 = t(_temp2);
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== isDisabled) {
    t8 = <EnvironmentIndicator environment="sandbox" disabled={isDisabled} />;
    $[16] = isDisabled;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== labelClassName || $[19] !== t8) {
    t9 = <span className={labelClassName}>{t8}</span>;
    $[18] = labelClassName;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t7 || $[22] !== t9) {
    t10 = {
      value: "sandbox",
      label: t7,
      component: t9
    };
    $[21] = t7;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t) {
    t11 = t(_temp3);
    $[24] = t;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== isDisabled) {
    t12 = <EnvironmentIndicator environment="production" disabled={isDisabled} />;
    $[26] = isDisabled;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== labelClassName || $[29] !== t12) {
    t13 = <span className={labelClassName}>{t12}</span>;
    $[28] = labelClassName;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t11 || $[32] !== t13) {
    t14 = {
      value: "production",
      label: t11,
      component: t13
    };
    $[31] = t11;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t10 || $[35] !== t14) {
    t15 = [t10, t14];
    $[34] = t10;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t) {
    t16 = t(_temp4);
    $[37] = t;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const t17 = className ?? "max-w-[148px] mx-auto [&_button]:shadow-sm [&_button]:rounded-(--button-border-radius)";
  let t18;
  if ($[39] !== handleValueChange || $[40] !== isDisabled || $[41] !== onOpenChange || $[42] !== selectedValue || $[43] !== t15 || $[44] !== t16 || $[45] !== t17) {
    t18 = <Select value={selectedValue} onChange={handleValueChange} options={t15} disabled={isDisabled} aria-label={t16} size="tiny" className={t17} onOpenChange={onOpenChange} />;
    $[39] = handleValueChange;
    $[40] = isDisabled;
    $[41] = onOpenChange;
    $[42] = selectedValue;
    $[43] = t15;
    $[44] = t16;
    $[45] = t17;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== t18 || $[48] !== t5 || $[49] !== t6) {
    t19 = <Tooltip content={t5} disabled={t6}>{t18}</Tooltip>;
    $[47] = t18;
    $[48] = t5;
    $[49] = t6;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  return t19;
};
export default SandboxToggle;
function _temp($) {
  return $.notAllowed;
}
function _temp2($_0) {
  return $_0.sandbox;
}
function _temp3($_1) {
  return $_1.production;
}
function _temp4($_2) {
  return $_2.ariaLabel;
}